import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';

const API_URL = process.env.REACT_APP_API_URL;

const formatBytes = (bytes) => {
  if (!bytes || bytes <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  const i = Math.min(units.length - 1, Math.floor(Math.log(bytes) / Math.log(1024)));
  return `${(bytes / Math.pow(1024, i)).toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
};

// Video ordering. `numeric` keeps "part 2" ahead of "part 10", and the
// case-insensitive sensitivity stops capitalisation from splitting the list.
const nameCollator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });

const formatDuration = (ms) => {
  if (ms == null || !isFinite(ms) || ms < 0) return '—';
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ${String(s % 60).padStart(2, '0')}s`;
  return `${Math.floor(m / 60)}h ${m % 60}m`;
};

function Videos() {
  const [videos, setVideos] = useState([]);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedFolders, setExpandedFolders] = useState(new Set());
  const [uploadEnabled, setUploadEnabled] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [processResult, setProcessResult] = useState(null);
  const [processTier, setProcessTier] = useState(''); // '' = backend default tier
  const [instanceStatus, setInstanceStatus] = useState(null); // null=unknown, { instance } or { instance: null }
  const [instanceStatusLoading, setInstanceStatusLoading] = useState(false);
  const [volume, setVolume] = useState(0.2);
  const [volumeUnlocked, setVolumeUnlocked] = useState(false);
  const [transferStats, setTransferStats] = useState(null);
  const [showLocalCache, setShowLocalCache] = useState(false);
  const [showMigrate, setShowMigrate] = useState(false);
  // Bulk S3 -> B2 migration. The job itself lives on the SERVER (see
  // bulkMigrationService) — these are just the controls and the last status we
  // fetched, so closing the tab or logging out doesn't stop a run.
  const [bulkFolder, setBulkFolder] = useState('*'); // '*' = all, '' = root-level only
  const [bulkMaxGb, setBulkMaxGb] = useState(0);     // 0 = no cap
  const [bulkJob, setBulkJob] = useState(null);
  const [filterLocalOnly, setFilterLocalOnly] = useState(false);
  const [rawQueueExpanded, setRawQueueExpanded] = useState(false);
  const [scrapeOpen, setScrapeOpen] = useState(false);
  const [scrapeForm, setScrapeForm] = useState({ pageUrl: '', playlistUrl: '', curl: '', folderFilename: '', quality: '' });
  const [scrapeMode, setScrapeMode] = useState('url'); // 'url' = page/playlist URL, 'curl' = paste a curl command
  const [scrapeJobs, setScrapeJobs] = useState([]); // [{ jobId, status, segmentsDone, segmentsTotal, bytesUploaded, speedBps, ... }]
  const [scrapeStatusLoading, setScrapeStatusLoading] = useState(false);
  const [dismissedScrapeJobs, setDismissedScrapeJobs] = useState(() => new Set());
  const [earlierScrapesOpen, setEarlierScrapesOpen] = useState(false);
  const sessionScrapeIdsRef = useRef(new Set()); // jobIds started during this page session — shown expanded; older server jobs collapse
  const scrapeSpeedRef = useRef({}); // jobId -> { bytes, time } for speed estimation between refreshes
  const scrapePrevStatusRef = useRef({}); // jobId -> last seen status, to detect completion/failure transitions
  const migratePrevStatusRef = useRef({}); // video id -> last seen migration status, to detect terminal transitions
  const bulkJobRef = useRef(null); // last seen bulk job, read inside the poller (which must not close over stale state)
  const [logsExpanded, setLogsExpanded] = useState(false);
  const [instanceLogs, setInstanceLogs] = useState([]);
  const [logsNextToken, setLogsNextToken] = useState(null);
  const [logsJobId, setLogsJobId] = useState(null);
  const [logsLoading, setLogsLoading] = useState(false);
  const [historyExpanded, setHistoryExpanded] = useState(false);
  const [historyRuns, setHistoryRuns] = useState([]);
  const [historyRunsLoading, setHistoryRunsLoading] = useState(false);
  const [historyRunsVisible, setHistoryRunsVisible] = useState(5);
  const [selectedHistoryRun, setSelectedHistoryRun] = useState(null);
  const [historyLogs, setHistoryLogs] = useState([]);
  const [historyLogsLoading, setHistoryLogsLoading] = useState(false);
  const [historyNextToken, setHistoryNextToken] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const videoRef = useRef(null);
  const lastTimeUpdate = useRef(0);
  const logsPanelRef = useRef(null);
  const menuRef = useRef(null);
  
  const { user, token, logout, sessionStatus, updateWatchTime, resetSession, isAuthenticated, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  // Fetch upload status from backend
  const fetchUploadStatus = async () => {
    try {
      const response = await fetch(`${API_URL}/videos/upload-status`, {
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setUploadEnabled(data.enabled);
      }
    } catch (err) {
      console.error('Error fetching upload status:', err);
    }
  };

  // Fetch Spot EC2 instance status
  const fetchInstanceStatus = async () => {
    try {
      setInstanceStatusLoading(true);
      const response = await fetch(`${API_URL}/videos/instance-status`, {
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setInstanceStatus(data);
      }
    } catch (err) {
      console.error('Error fetching instance status:', err);
    } finally {
      setInstanceStatusLoading(false);
    }
  };

  // Fetch CloudWatch logs for the running processing instance.
  // On first call (no token) fetches from the beginning; subsequent calls use
  // the nextToken to get only new lines.
  const fetchInstanceLogs = async (token_arg = null, reset = false) => {
    try {
      setLogsLoading(true);
      const url = token_arg
        ? `${API_URL}/videos/instance-logs?nextToken=${encodeURIComponent(token_arg)}`
        : `${API_URL}/videos/instance-logs`;
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });
      if (!response.ok) return;
      const data = await response.json();
      if (!data.configured || !data.jobId) {
        if (reset) {
          setInstanceLogs([]);
          setLogsNextToken(null);
          setLogsJobId(null);
        }
        return;
      }
      // If the job changed (new instance launched), reset log history
      setLogsJobId(prev => {
        if (prev && prev !== data.jobId) {
          setInstanceLogs(data.lines || []);
        } else {
          setInstanceLogs(prev2 => reset ? (data.lines || []) : [...prev2, ...(data.lines || [])]);
        }
        return data.jobId;
      });
      setLogsNextToken(data.nextToken || null);
    } catch (err) {
      console.error('Error fetching instance logs:', err);
    } finally {
      setLogsLoading(false);
    }
  };

  // Fetch the list of past processing run log streams
  const fetchLogHistory = async () => {
    try {
      setHistoryRunsLoading(true);
      const response = await fetch(`${API_URL}/videos/log-history`, {
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });
      if (!response.ok) return;
      const data = await response.json();
      setHistoryRuns(data.streams || []);
    } catch (err) {
      console.error('Error fetching log history:', err);
    } finally {
      setHistoryRunsLoading(false);
    }
  };

  // Fetch log lines for a specific past run
  const fetchHistoricalLogs = async (jobId, nextToken = null, reset = false) => {
    try {
      setHistoryLogsLoading(true);
      const params = new URLSearchParams({ jobId });
      if (nextToken) params.set('nextToken', nextToken);
      const response = await fetch(`${API_URL}/videos/historical-logs?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });
      if (!response.ok) return;
      const data = await response.json();
      setHistoryLogs(prev => reset ? (data.lines || []) : [...prev, ...(data.lines || [])]);
      setHistoryNextToken(data.nextToken || null);
    } catch (err) {
      console.error('Error fetching historical logs:', err);
    } finally {
      setHistoryLogsLoading(false);
    }
  };

  const selectHistoryRun = (jobId) => {
    if (selectedHistoryRun === jobId) {
      setSelectedHistoryRun(null);
      setHistoryLogs([]);
      setHistoryNextToken(null);
      return;
    }
    setSelectedHistoryRun(jobId);
    setHistoryLogs([]);
    setHistoryNextToken(null);
    fetchHistoricalLogs(jobId, null, true);
  };

  // Fetch AWS data transfer stats
  const fetchTransferStats = async () => {
    try {
      const response = await fetch(`${API_URL}/transfer/stats`, {
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setTransferStats(data);
      }
    } catch (err) {
      console.error('Error fetching transfer stats:', err);
    }
  };

  // Toggle upload functionality on backend
  const toggleUpload = async () => {
    try {
      const newState = !uploadEnabled;
      const response = await fetch(`${API_URL}/videos/upload-status`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({ enabled: newState })
      });
      
      if (response.ok) {
        const data = await response.json();
        setUploadEnabled(data.enabled);
      } else {
        console.error('Failed to toggle upload status');
      }
    } catch (err) {
      console.error('Error toggling upload:', err);
    }
  };

  // Open upload page in new tab
  const openUploadPage = () => {
    window.open(`${API_URL}/videos/upload`, '_blank');
  };

  const deleteRawVideo = async (filename) => {
    try {
      const response = await fetch(`${API_URL}/videos/raw/${encodeURIComponent(filename)}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        setProcessResult({ type: 'error', message: data.error || `Failed to delete ${filename}` });
        return;
      }
      setInstanceStatus(prev => {
        if (!prev) return prev;
        const newRawVideos = (prev.rawVideos ?? []).filter(v => v.name !== filename);
        return { ...prev, rawVideos: newRawVideos, rawVideoCount: newRawVideos.length };
      });
    } catch (err) {
      setProcessResult({ type: 'error', message: err.message });
    }
  };

  // Trigger video processing. Sends the selected speed tier (if any) so the
  // backend launches the matching Spot instance type; omitted = backend default.
  const processVideos = async () => {
    setProcessResult(null);
    try {
      setProcessing(true);
      const response = await fetch(`${API_URL}/videos/process`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify(processTier ? { tier: processTier } : {})
      });

      const data = await response.json();

      if (response.ok) {
        setProcessResult({ type: 'success', message: `Video processing job submitted! A ${data.tier ? `${data.tier} (${data.instanceType}) ` : ''}Spot EC2 instance will be launched to process ${data.rawVideoCount} raw video${data.rawVideoCount === 1 ? '' : 's'}. Job ID: ${data.jobId}` });
      } else if (response.status === 404) {
        setProcessResult({ type: 'error', message: 'No videos available for processing in the raw folder.' });
      } else {
        setProcessResult({ type: 'error', message: `Failed to start processing: ${data.error || 'Unknown error'}` });
      }
    } catch (err) {
      console.error('Error processing videos:', err);
      setProcessResult({ type: 'error', message: `Error: ${err.message}` });
    } finally {
      setProcessing(false);
    }
  };

  // Fetch all scrape jobs. Per-job speed is the byte delta since the previous
  // refresh, falling back to the average since the job started when there's no
  // earlier sample to diff against. `silent` skips the loading state so the
  // auto-refresh poll doesn't flicker the ↻ button.
  const fetchScrapeStatus = async ({ silent = false } = {}) => {
    try {
      if (!silent) setScrapeStatusLoading(true);
      const response = await fetch(`${API_URL}/videos/scrape-status`, {
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });
      if (!response.ok) return;
      const data = await response.json();
      const now = Date.now();
      const jobs = (data.jobs || []).map(job => {
        let speed = 0;
        if (job.status === 'running') {
          const prev = scrapeSpeedRef.current[job.jobId];
          if (prev && now > prev.time && job.bytesUploaded >= prev.bytes) {
            speed = ((job.bytesUploaded - prev.bytes) * 1000) / (now - prev.time);
          } else {
            const elapsed = now - new Date(job.startedAt).getTime();
            speed = elapsed > 0 ? (job.bytesUploaded * 1000) / elapsed : 0;
          }
        }
        scrapeSpeedRef.current[job.jobId] = { bytes: job.bytesUploaded, time: now };

        const prevStatus = scrapePrevStatusRef.current[job.jobId];
        if (prevStatus === 'running' && job.status === 'completed') {
          setProcessResult({ type: 'success', message: `Scrape complete: ${job.s3Key} (${job.segmentsTotal} segments, ${job.quality})` });
          fetchInstanceStatus(); // refresh raw-queue count so the new .ts shows up
        } else if (prevStatus === 'running' && job.status === 'failed') {
          setProcessResult({ type: 'error', message: `Scrape failed: ${job.error || 'Unknown error'}` });
        }
        scrapePrevStatusRef.current[job.jobId] = job.status;

        return { ...job, speedBps: speed };
      });
      setScrapeJobs(jobs);
    } catch (err) {
      console.error('Error fetching scrape status:', err);
    } finally {
      if (!silent) setScrapeStatusLoading(false);
    }
  };

  // Start a scrape job: backend downloads the HLS stream from the page URL
  // and streams it straight into raw/<FOLDER_FILENAME>.ts on S3.
  const startScrape = async () => {
    setProcessResult(null);
    try {
      const body = {
        folderFilename: scrapeForm.folderFilename.trim()
      };
      if (scrapeMode === 'curl') {
        // The backend extracts the playlist URL and request headers from the
        // curl command and replays those headers on every request.
        body.curl = scrapeForm.curl.trim();
      } else {
        // Page URL is optional when a playlist.m3u8 URL is given directly; when
        // both are present the backend uses the page URL as the Referer.
        if (scrapeForm.pageUrl.trim()) body.pageUrl = scrapeForm.pageUrl.trim();
        if (scrapeForm.playlistUrl.trim()) body.playlistUrl = scrapeForm.playlistUrl.trim();
      }
      if (scrapeForm.quality) body.quality = scrapeForm.quality;
      const response = await fetch(`${API_URL}/videos/scrape`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify(body)
      });
      const data = await response.json();
      if (response.status === 202) {
        scrapePrevStatusRef.current[data.jobId] = 'running';
        sessionScrapeIdsRef.current.add(data.jobId);
        setScrapeJobs(prev => [
          // The 202 response carries status 'started'; the UI only knows
          // running/completed/failed, so force 'running' until the next poll.
          { segmentsDone: 0, segmentsTotal: 0, bytesUploaded: 0, speedBps: 0, startedAt: new Date().toISOString(), ...data, status: 'running' },
          ...prev
        ]);
        setScrapeForm(f => ({ ...f, pageUrl: '', playlistUrl: '', curl: '', folderFilename: '' }));
        setProcessResult({ type: 'success', message: `Scrape started → ${data.s3Key}` });
      } else {
        setProcessResult({ type: 'error', message: `Failed to start scrape: ${data.error || 'Unknown error'}` });
      }
    } catch (err) {
      console.error('Error starting scrape:', err);
      setProcessResult({ type: 'error', message: `Error: ${err.message}` });
    }
  };

  const dismissScrapeJob = (jobId) => {
    setDismissedScrapeJobs(prev => {
      const next = new Set(prev);
      next.add(jobId);
      return next;
    });
  };

  // Close the header menu when clicking/tapping outside it
  useEffect(() => {
    if (!menuOpen) return undefined;
    const handlePointerDown = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('pointerdown', handlePointerDown);
    return () => document.removeEventListener('pointerdown', handlePointerDown);
  }, [menuOpen]);

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/');
    }
  }, [isAuthenticated, authLoading, navigate]);

  // Fetch videos only when authenticated
  useEffect(() => {
    if (isAuthenticated && token) {
      fetchVideos();
      fetchUploadStatus();
      fetchInstanceStatus();
      fetchTransferStats();
      fetchScrapeStatus(); // pick up scrapes already running on the server
      fetchBulkJob();     // ...and a bulk migration already running on the server
    }
  }, [isAuthenticated, token]);

  // Sync volume state to video element
  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.volume = volume;
    }
  }, [volume, selectedVideo]);

  // Fetch the server-side bulk migration job.
  //
  // Called on mount as well as from the poller, and that mount call is the
  // whole point of the server-side design: reopen the tab or log back in
  // mid-run and the bar reappears still counting, because the job never
  // stopped. Also the one place the finished-job banner fires.
  const fetchBulkJob = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/videos/migrate-bulk`, {
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });
      if (!response.ok) return;
      const data = await response.json();
      const job = data.job || null;
      const prev = bulkJobRef.current;

      // One-shot summary on the running -> settled transition. Guarded by the
      // ref rather than state so a second call in the same tick can't re-fire it.
      if (prev && prev.status === 'running' && job && job.status !== 'running') {
        const bad = job.failed + job.rejected;
        const parts = [`${job.succeeded} migrated`];
        if (job.warnings > 0) parts.push(`${job.warnings} left on S3`);
        if (bad > 0) parts.push(`${bad} failed`);
        setProcessResult({
          type: bad > 0 ? 'error' : 'success',
          message: `Bulk migration ${job.status === 'cancelled' ? 'stopped' : 'finished'} — ${parts.join(', ')}`
        });
      }

      bulkJobRef.current = job;
      setBulkJob(job);
    } catch { /* ignore transient poll errors */ }
  }, [token]);

  const bulkRunning = !!bulkJob && bulkJob.status === 'running';

  // Silently poll download progress without triggering loading state or re-rendering the full list.
  const anyDownloading = videos.some((v) => v.download);
  // Only 'running' — a lingering failed/warning entry would otherwise keep
  // the poll alive for the whole of its server-side TTL.
  const anyMigrating = videos.some((v) => v.migration && v.migration.status === 'running');
  useEffect(() => {
    if (!anyDownloading && !anyMigrating && !bulkRunning) return undefined;
    const poll = async () => {
      try {
        const response = await fetch(`${API_URL}/videos`, {
          headers: { 'Authorization': `Bearer ${token}` },
          credentials: 'include'
        });
        if (!response.ok) return;
        const fresh = await response.json();

        // Fire the one-shot migration banners HERE, before touching state.
        // The setVideos updater below must stay pure — StrictMode double-
        // invokes it in development, which would double-fire these.
        for (const f of fresh) {
          const prevStatus = migratePrevStatusRef.current[f.id];
          const status = f.migration && f.migration.status;
          if (prevStatus === 'running') {
            if (status === 'failed') {
              setProcessResult({ type: 'error', message: `Migration failed: ${f.name} — ${f.migration.error}` });
            } else if (status === 'warning') {
              setProcessResult({ type: 'error', message: `Copied to B2, but the S3 original remains: ${f.migration.error}` });
            } else if (!status && f.source === 'b2') {
              // Success leaves no migration entry behind — the catalog is the signal.
              //
              // Suppressed during a bulk run: hundreds of these would overwrite
              // each other into noise, and the run posts one summary at the end.
              // Failures and warnings are NOT suppressed — those need attention.
              // Read the ref, not state: this closure would hold a stale copy.
              if (!bulkJobRef.current || bulkJobRef.current.status !== 'running') {
                setProcessResult({ type: 'success', message: `Migrated to B2: ${f.name} — S3 original deleted` });
              }
            }
          }
          if (status) {
            migratePrevStatusRef.current[f.id] = status;
          } else {
            delete migratePrevStatusRef.current[f.id];
          }
        }

        setVideos(prev => prev.map(v => {
          const update = fresh.find(f => f.id === v.id);
          if (!update) return v;
          return {
            ...v,
            localCached: update.localCached,
            download: update.download,
            source: update.source,
            alsoOnS3: update.alsoOnS3,
            migration: update.migration
          };
        }));
        if (bulkRunning) await fetchBulkJob();
      } catch { /* ignore transient poll errors */ }
    };
    const interval = setInterval(poll, 2000);
    return () => clearInterval(interval);
  }, [anyDownloading, anyMigrating, bulkRunning, fetchBulkJob, token]); // fetchVideos intentionally excluded — poll is self-contained

  // Auto-refresh scrape status while any scrape is running, so progress and
  // the completion/failure transition show up without pressing ↻. Stops as
  // soon as no job is in 'running' state.
  const anyScrapeRunning = scrapeJobs.some(j => j.status === 'running');
  useEffect(() => {
    if (!anyScrapeRunning) return undefined;
    const interval = setInterval(() => fetchScrapeStatus({ silent: true }), 3000);
    return () => clearInterval(interval);
  }, [anyScrapeRunning, token]); // fetchScrapeStatus intentionally excluded — poll is self-contained

  const scrapeActiveCount = scrapeJobs.filter(j => j.status === 'running').length;
  const visibleScrapeJobs = scrapeJobs.filter(j => !dismissedScrapeJobs.has(j.jobId));
  // Jobs started in this page session stay expanded; jobs inherited from the
  // server (previous sessions / reloads) sit behind a collapsed toggle.
  const sessionScrapeJobs = visibleScrapeJobs.filter(j => sessionScrapeIdsRef.current.has(j.jobId));
  const earlierScrapeJobs = visibleScrapeJobs.filter(j => !sessionScrapeIdsRef.current.has(j.jobId));

  const renderScrapeJobCard = (job) => {
    const name = (job.s3Key || '').replace(/^raw\//, '');
    const total = job.segmentsTotal || 0;
    const pct = total > 0 ? Math.min(100, Math.floor((job.segmentsDone / total) * 100)) : null;
    const elapsed = (job.finishedAt ? new Date(job.finishedAt) : Date.now()) - new Date(job.startedAt);
    const eta = job.status === 'running' && total > 0 && job.segmentsDone > 0
      ? (elapsed / job.segmentsDone) * (total - job.segmentsDone)
      : null;
    return (
      <div key={job.jobId} className={`scrape-job-card ${job.status}`}>
        <div className="scrape-job-header">
          <span className="scrape-job-name" title={job.pageUrl || name}>
            {job.status === 'running' ? '⏳' : job.status === 'completed' ? '✅' : '❌'} {name}
          </span>
          {job.quality && <span className="scrape-job-quality">{job.quality}</span>}
          {job.status === 'running' && (
            <span className="scrape-job-stats">
              <span className="scrape-job-speed">{job.speedBps > 0 ? `${formatBytes(job.speedBps)}/s` : 'starting…'}</span>
              <span className="scrape-job-pct">{pct != null ? `${pct}%` : '…'}</span>
            </span>
          )}
          {job.status !== 'running' && (
            <button
              className="scrape-job-dismiss"
              onClick={() => dismissScrapeJob(job.jobId)}
              title="Dismiss"
            >✕</button>
          )}
        </div>
        <div className="scrape-progress-track">
          <div
            className={`scrape-progress-fill ${job.status}${pct == null && job.status === 'running' ? ' indeterminate' : ''}`}
            style={{ width: job.status === 'completed' ? '100%' : `${pct ?? 100}%` }}
          />
        </div>
        <div className="scrape-job-meta">
          {job.status === 'running' && (
            <>
              <span>{job.segmentsDone}/{total || '?'} segments</span>
              <span>{formatBytes(job.bytesUploaded)} uploaded</span>
              <span>{eta != null ? `~${formatDuration(eta)} remaining` : 'estimating…'}</span>
            </>
          )}
          {job.status === 'completed' && (
            <>
              <span>{job.segmentsTotal} segments</span>
              <span>{formatBytes(job.bytesUploaded)}</span>
              <span>took {formatDuration(elapsed)}</span>
            </>
          )}
          {job.status === 'failed' && (
            <span className="scrape-job-error" title={job.error || ''}>{job.error || 'Unknown error'}</span>
          )}
        </div>
      </div>
    );
  };

  // Poll CloudWatch logs every 10 s while the log panel is open and an instance is running.
  // When the panel is first opened, reset the log history and fetch from scratch.
  const instanceRunning = !!(instanceStatus?.instance);
  useEffect(() => {
    if (!logsExpanded || !instanceRunning) return;
    // Initial fetch from scratch
    setInstanceLogs([]);
    setLogsNextToken(null);
    setLogsJobId(null);
    fetchInstanceLogs(null, true);
  }, [logsExpanded, instanceRunning]); // eslint-disable-line

  // Auto-scroll log panel to bottom when new lines arrive
  useEffect(() => {
    if (logsExpanded && logsPanelRef.current) {
      logsPanelRef.current.scrollTop = logsPanelRef.current.scrollHeight;
    }
  }, [instanceLogs, logsExpanded]);

  // Fetch past-run list when the history panel is opened
  useEffect(() => {
    if (!historyExpanded) return;
    fetchLogHistory();
  }, [historyExpanded]); // eslint-disable-line

  // Handle video time updates for buffer tracking
  const handleTimeUpdate = useCallback(() => {
    if (!videoRef.current || !selectedVideo) return;
    
    const currentTime = videoRef.current.currentTime * 1000; // Convert to ms
    
    // Update server every 5 seconds of playback
    if (currentTime - lastTimeUpdate.current >= 5000) {
      lastTimeUpdate.current = currentTime;
      updateWatchTime(currentTime, selectedVideo.id);
    }
  }, [updateWatchTime, selectedVideo]);

  const fetchVideos = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch(`${API_URL}/videos`, {
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });
      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Please login to view videos');
        }
        throw new Error('Failed to fetch videos');
      }
      const data = await response.json();
      setVideos(data);
    } catch (err) {
      setError(err.message);
      console.error('Error fetching videos:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadLocal = async (video, e) => {
    e.stopPropagation();
    try {
      const response = await fetch(`${API_URL}/videos/${video.id}/download`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });
      if (!response.ok && response.status !== 409) {
        const data = await response.json().catch(() => ({}));
        const msg = data.error || `Failed to start download (${response.status})`;
        setProcessResult({ type: 'error', message: msg });
        return;
      }
      // Optimistic update — flip the button to "Downloading…" immediately.
      // The silent poll will take over and update progress from here.
      setVideos((prev) => prev.map((v) =>
        v.id === video.id
          ? { ...v, download: { bytesDownloaded: 0, totalBytes: v.size } }
          : v
      ));
    } catch (err) {
      setProcessResult({ type: 'error', message: err.message });
    }
  };

  const handleDeleteLocal = async (video, e) => {
    e.stopPropagation();
    try {
      const response = await fetch(`${API_URL}/videos/${video.id}/local`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });
      if (!response.ok && response.status !== 404) {
        throw new Error(`Failed to delete local copy (${response.status})`);
      }
      fetchVideos();
    } catch (err) {
      console.error('Error deleting local copy:', err);
    }
  };

  const renderLocalCacheButton = (video) => {
    if (video.download) {
      const total = video.download.totalBytes || video.size || 0;
      const pct = total > 0
        ? Math.min(99, Math.floor((video.download.bytesDownloaded / total) * 100))
        : 0;
      return (
        <button className="local-cache-button downloading" disabled>
          ⏳ Downloading… {pct}%
        </button>
      );
    }
    if (video.localCached) {
      return (
        <button
          className="local-cache-button cached"
          onClick={(e) => handleDeleteLocal(video, e)}
          title="Remove the local copy; future plays will stream from S3"
        >
          🗑️ Remove local
        </button>
      );
    }
    return (
      <button
        className="local-cache-button"
        onClick={(e) => handleDownloadLocal(video, e)}
        title="Download this video to the server's local disk so future plays don't hit S3"
      >
        📥 Download to local
      </button>
    );
  };

  // Migrate one video S3 -> B2. Fires on a single click, like the app's other
  // destructive actions (deleteRawVideo, handleDeleteLocal). Note this is not
  // recoverable from the UI: the API deletes the S3 original once it has
  // verified the B2 copy, so the button title spells that out.
  const handleMigrate = async (video, e) => {
    e.stopPropagation();

    try {
      const response = await fetch(`${API_URL}/videos/${video.id}/migrate`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        setProcessResult({ type: 'error', message: data.error || `Failed to start migration (${response.status})` });
        return;
      }
      // Seed the transition tracker before the first poll. A verify-only run
      // can finish inside 2s, and without this its terminal status would be
      // the first one the poller ever saw — so no banner would fire.
      migratePrevStatusRef.current[video.id] = 'running';
      // Optimistic update — this is also what starts the poller, so without it
      // nothing would report progress for the first couple of seconds.
      setVideos((prev) => prev.map((v) =>
        v.id === video.id
          ? { ...v, migration: { status: 'running', phase: data.plan === 'verify-only' ? 'verifying' : 'copying', bytesCopied: 0, totalBytes: v.size } }
          : v
      ));
    } catch (err) {
      setProcessResult({ type: 'error', message: err.message });
    }
  };

  // Start a bulk run. The server does its own selection from the live catalog —
  // bulkPlan below is only the label's preview — and the 202 body is the real
  // thing, so seed state from the response rather than from the preview.
  const handleBulkMigrate = async () => {
    try {
      const response = await fetch(`${API_URL}/videos/migrate-bulk`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ folder: bulkFolder === '*' ? null : bulkFolder, maxGb: bulkMaxGb })
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        setProcessResult({ type: 'error', message: data.error || `Failed to start bulk migration (${response.status})` });
        return;
      }
      bulkJobRef.current = data;
      setBulkJob(data); // also what starts the poller
      setProcessResult({
        type: 'success',
        message: `Bulk migration started — ${data.total} files (${data.copyCount} to copy, ${data.verifyCount} verify-only). It runs on the server; you can close this tab.`
      });
    } catch (err) {
      setProcessResult({ type: 'error', message: err.message });
    }
  };

  // Stop queueing. Migrations the server already accepted still finish — there
  // is no abort for one, and killing a copy mid-flight orphans billable
  // multipart parts on B2. The job stays 'running' until that tail drains.
  const handleBulkStop = async () => {
    try {
      const response = await fetch(`${API_URL}/videos/migrate-bulk/stop`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        setProcessResult({ type: 'error', message: data.error || `Failed to stop (${response.status})` });
        return;
      }
      bulkJobRef.current = data;
      setBulkJob(data);
    } catch (err) {
      setProcessResult({ type: 'error', message: err.message });
    }
  };

  const renderMigrateButton = (video) => {
    const migration = video.migration;

    if (migration && migration.status === 'running') {
      const total = migration.totalBytes || video.size || 0;
      const pct = total > 0 ? Math.min(99, Math.floor((migration.bytesCopied / total) * 100)) : 0;
      // Name every phase explicitly. A phase this switch doesn't know (the
      // server's brief 'preflight' reservation, or anything added later) must
      // not fall through to the destructive-sounding delete label.
      const label = migration.phase === 'copying'
        ? `🚚 Copying… ${pct}%`
        : migration.phase === 'verifying' ? '🚚 Verifying…'
          : migration.phase === 'deleting' ? '🚚 Deleting S3…'
            : '🚚 Starting…';
      return <button className="migrate-button running" disabled>{label}</button>;
    }
    if (migration && migration.status === 'failed') {
      return (
        <button className="migrate-button failed" onClick={(e) => handleMigrate(video, e)} title={migration.error}>
          ⚠️ Retry migrate
        </button>
      );
    }
    if (migration && migration.status === 'warning') {
      return (
        <button className="migrate-button warning" onClick={(e) => handleMigrate(video, e)} title={migration.error}>
          ⚠️ S3 copy remains
        </button>
      );
    }

    // Fully on B2 with no S3 original left — nothing to migrate.
    if (video.source === 'b2' && !video.alsoOnS3) return null;

    // Already copied to B2 by rclone: the server skips the transfer and only
    // verifies before freeing the S3 side, so say so rather than promising a copy.
    const verifyOnly = video.source === 'b2' && video.alsoOnS3;
    return (
      <button
        className="migrate-button"
        onClick={(e) => handleMigrate(video, e)}
        title={verifyOnly
          ? 'Already on Backblaze B2 — verify the copy, then permanently delete the original from S3'
          : 'Copy to Backblaze B2, verify the copy, then permanently delete the original from S3'}
      >
        {verifyOnly ? '✔️ Verify & free S3' : '🚚 Migrate to B2'}
      </button>
    );
  };

  const renderMigrateProgress = (video) => {
    const migration = video.migration;
    if (!migration || migration.status !== 'running') return null;
    const total = migration.totalBytes || video.size || 0;
    const copying = migration.phase === 'copying';
    const pct = copying && total > 0 ? Math.min(100, Math.floor((migration.bytesCopied / total) * 100)) : 100;
    return (
      <div className="migrate-progress">
        <div className="scrape-progress-track">
          <div
            className={`scrape-progress-fill${copying ? '' : ' indeterminate'}`}
            style={{ width: `${pct}%` }}
          />
        </div>
      </div>
    );
  };

  const handleVideoSelect = async (video) => {
    // Reset session tracking for new video
    lastTimeUpdate.current = 0;
    await resetSession(video.id); // Reset buffer tracking for new video
    setSelectedVideo(video);
    // Reset video element when selecting new video
    if (videoRef.current) {
      videoRef.current.load();
    }
  };

  const getStreamUrl = (videoId) => {
    return `${API_URL}/videos/${videoId}/stream`;
  };

  // Which bucket backs this video. Only meaningful during the S3 -> B2
  // migration; once everything is on B2 the badge is uniform and can go.
  const renderSourceBadge = (video) => {
    if (!video.source) return null;
    const onB2 = video.source === 'b2';
    return (
      <span
        className={`source-badge ${onB2 ? 'b2' : 's3'}`}
        title={onB2
          ? (video.alsoOnS3
              ? 'Served from Backblaze B2 \u2014 the S3 copy is still there and can be deleted'
              : 'Served from Backblaze B2')
          : 'Served from AWS S3 \u2014 not migrated yet'}
      >
        {onB2 ? 'B2' : 'S3'}
      </span>
    );
  };

  // Migration progress, so it is visible without opening a bucket console.
  const sourceCounts = React.useMemo(() => ({
    total: videos.length,
    onB2: videos.filter(v => v.source === 'b2').length,
    hasSource: videos.some(v => v.source)
  }), [videos]);

  // One renderer for both the root-level and in-folder rows. They were
  // duplicated; with a second (destructive) action on each row, two copies
  // would drift on a control that deletes objects from S3.
  const renderVideoRow = (video, { nested = false } = {}) => (
    <div
      key={video.id}
      className={`video-item${nested ? ' nested' : ''}${selectedVideo?.id === video.id ? ' active' : ''}`}
      onClick={() => handleVideoSelect(video)}
    >
      <div className="video-content">
        <span className="video-icon">🎥</span>
        <div className="video-details">
          <h4>{video.name}</h4>
          <span className="size">
            {video.sizeFormatted}
            {renderSourceBadge(video)}
          </span>
          {renderMigrateProgress(video)}
        </div>
        {(showLocalCache || showMigrate) ? (
          <div className="video-actions">
            {showLocalCache && renderLocalCacheButton(video)}
            {showMigrate && renderMigrateButton(video)}
          </div>
        ) : video.localCached && (
          <span className="cached-indicator" title="Cached locally">💾</span>
        )}
      </div>
    </div>
  );

  const toggleFolder = (folderName) => {
    setExpandedFolders(prev => {
      const newSet = new Set(prev);
      if (newSet.has(folderName)) {
        newSet.delete(folderName);
      } else {
        newSet.add(folderName);
      }
      return newSet;
    });
  };

  // Group videos by folder.
  //
  // The API concatenates the per-store catalogs (S3 first, then the B2-only
  // keys), so without a sort here the list is ordered by bucket rather than by
  // name and a migrated video jumps to the bottom. Sort the merged list by
  // folder then name so the order is identical whatever store — S3, B2, or a
  // local cache copy — happens to back each video. Sorting before grouping
  // also fixes the folder order — folders is a Map, which iterates in
  // insertion order for every key (a plain object would hoist an
  // integer-looking folder name like "2024" to the front).
  // Folder options for the bulk bar. Derived from the full catalog, NOT from
  // groupedVideos — that one is narrowed by the "Local only" filter, which has
  // nothing to do with what can be migrated.
  const bulkFolders = React.useMemo(
    () => [...new Set(videos.map(v => v.folder).filter(Boolean))].sort(nameCollator.compare),
    [videos]
  );

  // Preview of what a bulk run would pick up.
  //
  // ADVISORY ONLY. This mirrors selectCandidates in bulkMigrationService so the
  // button label is instant and no bucket listing happens on every dropdown
  // change — but the SERVER re-selects from the live catalog when the run
  // starts, and its selection is the one that decides what gets deleted from
  // S3. The 202 response replaces these numbers. Don't mistake the two for one
  // source of truth; if they disagree, the server is right.
  const bulkPlan = React.useMemo(() => {
    const maxBytes = bulkMaxGb > 0 ? bulkMaxGb * 1024 * 1024 * 1024 : 0;
    const eligible = videos
      .filter(v => {
        if (v.source === 'b2' && !v.alsoOnS3) return false;          // nothing left to do
        if (v.migration && v.migration.status === 'running') return false;
        if (bulkFolder === '*') return true;
        if (bulkFolder === '') return !v.folder;
        return v.folder === bulkFolder;
      })
      .slice()
      .sort((a, b) =>
        nameCollator.compare(a.folder || '', b.folder || '') ||
        nameCollator.compare(a.name || '', b.name || ''));

    let copyBytes = 0, copyCount = 0, verifyCount = 0, fileCount = 0;
    for (const v of eligible) {
      // Already on B2 — the server skips the transfer, so this costs no bytes
      // and must not be charged against the cap.
      if (v.source === 'b2' && v.alsoOnS3) { verifyCount += 1; fileCount += 1; continue; }
      // Skip-and-continue: one oversized file must not block the smaller ones
      // behind it that still fit.
      if (maxBytes > 0 && copyBytes + v.size > maxBytes) continue;
      copyBytes += v.size; copyCount += 1; fileCount += 1;
    }
    return { fileCount, copyBytes, copyCount, verifyCount };
  }, [videos, bulkFolder, bulkMaxGb]);

  const groupedVideos = React.useMemo(() => {
    const groups = {
      root: [],
      folders: new Map()
    };

    const filteredVideos = (filterLocalOnly ? videos.filter(v => v.localCached) : videos)
      .slice()
      .sort((a, b) =>
        nameCollator.compare(a.folder || '', b.folder || '') ||
        nameCollator.compare(a.name || '', b.name || ''));

    filteredVideos.forEach(video => {
      if (video.folder) {
        if (!groups.folders.has(video.folder)) {
          groups.folders.set(video.folder, []);
        }
        groups.folders.get(video.folder).push(video);
      } else {
        groups.root.push(video);
      }
    });

    return groups;
  }, [videos, filterLocalOnly]);

  if (authLoading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div className="app">
      <header className="header">
        <h1 className="site-title">🎬 <span className="site-title-text">WebsiteSensei</span></h1>
        {isAuthenticated && (
          <div className="header-right">
            <span className="user-chip">👤 {user.username || user.name}</span>
            <div className="header-menu" ref={menuRef}>
              <button
                className={menuOpen ? "menu-button open" : "menu-button"}
                onClick={() => setMenuOpen(v => !v)}
                aria-haspopup="true"
                aria-expanded={menuOpen}
                title="Menu"
              >
                ☰
                {scrapeActiveCount > 0 && (
                  <span className="scrape-count-badge">{scrapeActiveCount}</span>
                )}
              </button>
              {menuOpen && (
                <div className="menu-dropdown">
                  <div className="menu-user">Signed in as <strong>{user.username || user.name}</strong></div>
                  <button
                    className="menu-item"
                    onClick={() => { setMenuOpen(false); processVideos(); }}
                    disabled={processing}
                    title="Process raw videos with ffmpeg on AWS Spot EC2"
                  >
                    {processing ? '⏳ Submitting…' : '🎬 Process videos'}
                  </button>
                  {Object.keys(instanceStatus?.tiers ?? {}).length > 1 && (
                    <label className="menu-tier-row" title="Spot EC2 speed tier for processing">
                      <span className="menu-tier-label">⚡ Speed tier</span>
                      <select
                        className="menu-tier-select"
                        value={processTier || instanceStatus.defaultTier || ''}
                        onChange={e => setProcessTier(e.target.value)}
                        disabled={processing}
                      >
                        {Object.entries(instanceStatus.tiers).map(([name, t]) => (
                          <option key={name} value={name}>
                            {name} · {t.instanceType}
                          </option>
                        ))}
                      </select>
                    </label>
                  )}
                  <button
                    className="menu-item"
                    onClick={toggleUpload}
                    title={uploadEnabled ? "Disable upload functionality" : "Enable upload functionality"}
                  >
                    <span>{uploadEnabled ? '🔓 Uploads on' : '🔒 Uploads off'}</span>
                    <span className={uploadEnabled ? "menu-switch on" : "menu-switch"} aria-hidden="true"></span>
                  </button>
                  {uploadEnabled && (
                    <button
                      className="menu-item"
                      onClick={() => { setMenuOpen(false); openUploadPage(); }}
                      title="Open upload page in new tab"
                    >
                      📤 Upload video
                    </button>
                  )}
                  {uploadEnabled && (
                    <button
                      className="menu-item"
                      onClick={() => { setMenuOpen(false); setScrapeOpen(v => !v); }}
                      title="Scrape a video from a page URL into the raw queue"
                    >
                      <span>🔗 Scrape URL</span>
                      {scrapeActiveCount > 0 && (
                        <span className="scrape-count-badge">{scrapeActiveCount}</span>
                      )}
                    </button>
                  )}
                  <div className="menu-divider" aria-hidden="true"></div>
                  <button
                    className="menu-item danger"
                    onClick={() => { logout(); navigate('/'); }}
                  >
                    🚪 Log out
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </header>

      {instanceStatus && instanceStatus.configured && (
        <>
          <div className={`instance-status-bar ${instanceStatus.instance ? 'running' : 'idle'}`}>
            <span className="instance-status-dot"></span>
            {instanceStatus.instance ? (
              <span className="instance-status-text">
                Spot instance <strong>{instanceStatus.instance.instanceId}</strong> is{' '}
                <strong>{instanceStatus.instance.state}</strong>
                {instanceStatus.instance.launchTime && (
                  <> &mdash; launched {new Date(instanceStatus.instance.launchTime).toLocaleTimeString()}</>
                )}
                <> &mdash; <button className="raw-queue-toggle" onClick={() => setRawQueueExpanded(v => !v)}>
                  <strong>{instanceStatus.rawVideoCount ?? "N/A"}</strong> raw video{(instanceStatus.rawVideoCount ?? 0) === 1 ? '' : 's'} queued
                </button> {(instanceStatus.rawVideoCount ?? 0) > 0 && <span className="raw-queue-chevron">{rawQueueExpanded ? '▲' : '▼'}</span>}</>
              </span>
            ) : (
              <span className="instance-status-text">
                No processing instance running
                <> &mdash; <button className="raw-queue-toggle" onClick={() => setRawQueueExpanded(v => !v)}>
                  <strong>{instanceStatus.rawVideoCount ?? "N/A"}</strong> raw video{(instanceStatus.rawVideoCount ?? 0) === 1 ? '' : 's'} queued
                </button> {(instanceStatus.rawVideoCount ?? 0) > 0 && <span className="raw-queue-chevron">{rawQueueExpanded ? '▲' : '▼'}</span>}</>
              </span>
            )}
            <button
              className="instance-status-refresh"
              onClick={fetchInstanceStatus}
              disabled={instanceStatusLoading}
              title="Refresh instance status"
            >
              {instanceStatusLoading ? 'Checking…' : 'Refresh'}
            </button>
          </div>
          {rawQueueExpanded && (instanceStatus.rawVideos?.length ?? 0) > 0 && (
            <div className="raw-queue-panel">
              {instanceStatus.rawVideos.map(video => (
                <div key={video.key} className="raw-queue-item">
                  <span className="raw-queue-item-name" title={video.name}>{video.name}</span>
                  <button
                    className="raw-queue-delete"
                    onClick={() => deleteRawVideo(video.name)}
                  >
                    Delete
                  </button>
                </div>
              ))}
            </div>
          )}
          {rawQueueExpanded && (instanceStatus.rawVideoCount ?? 0) === 0 && (
            <div className="raw-queue-panel raw-queue-empty">No raw videos queued.</div>
          )}
          {instanceStatus.instance && (
            <>
              <div className="instance-logs-bar">
                <button
                  className="raw-queue-toggle"
                  onClick={() => setLogsExpanded(v => !v)}
                >
                  📋 Processing Logs {logsExpanded ? '▲' : '▼'}
                </button>
                {logsExpanded && (
                  <span className="instance-logs-meta">
                    {logsJobId ? `Job ${logsJobId.substring(0, 8)}…` : ''}
                    {logsLoading
                      ? <span className="instance-logs-spinner"> Loading…</span>
                      : <button
                          className="logs-refresh-btn"
                          onClick={() => fetchInstanceLogs(logsNextToken, false)}
                          title="Refresh logs"
                        >↻ Refresh</button>
                    }
                  </span>
                )}
              </div>
              {logsExpanded && (
                <div className="instance-logs-panel" ref={logsPanelRef}>
                  {instanceLogs.length === 0 && !logsLoading && (
                    <span className="instance-logs-empty">No log output yet — CloudWatch agent may still be starting.</span>
                  )}
                  {instanceLogs.map((line, i) => (
                    <div key={i} className="instance-log-line">
                      <span className="instance-log-ts">
                        {new Date(line.timestamp).toLocaleTimeString('en-CA', { timeZone: 'America/Toronto' })}
                      </span>
                      <span className="instance-log-msg">{line.message}</span>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
          <div className="instance-logs-bar">
            <button
              className="raw-queue-toggle"
              onClick={() => { setHistoryExpanded(v => !v); setHistoryRunsVisible(5); }}
            >
              Past Runs {historyExpanded ? '▲' : '▼'}
            </button>
            {historyExpanded && historyRunsLoading && (
              <span className="instance-logs-spinner">Loading…</span>
            )}
          </div>
          {historyExpanded && (
            <div className="history-runs-panel">
              {historyRuns.length === 0 && !historyRunsLoading && (
                <span className="instance-logs-empty">No past runs found in CloudWatch.</span>
              )}
              {historyRuns.slice(0, historyRunsVisible).map(run => {
                const label = new Date(run.lastEventTime || run.creationTime)
                  .toLocaleString('en-CA', { timeZone: 'America/Toronto', year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
                const isSelected = selectedHistoryRun === run.jobId;
                return (
                  <div key={run.jobId}>
                    <button
                      className={`history-run-btn${isSelected ? ' history-run-selected' : ''}`}
                      title={run.jobId}
                      onClick={() => selectHistoryRun(run.jobId)}
                    >
                      {label}
                      {historyLogsLoading && isSelected && <span className="instance-logs-spinner"> Loading…</span>}
                    </button>
                    {isSelected && (
                      <div className="instance-logs-panel history-logs-panel">
                        {historyLogs.length === 0 && !historyLogsLoading && (
                          <span className="instance-logs-empty">No log output found for this run.</span>
                        )}
                        {historyLogs.map((line, i) => (
                          <div key={i} className="instance-log-line">
                            <span className="instance-log-ts">
                              {new Date(line.timestamp).toLocaleTimeString('en-CA', { timeZone: 'America/Toronto' })}
                            </span>
                            <span className="instance-log-msg">{line.message}</span>
                          </div>
                        ))}
                        {historyNextToken && !historyLogsLoading && (
                          <button
                            className="history-load-more"
                            onClick={() => fetchHistoricalLogs(run.jobId, historyNextToken, false)}
                          >
                            Load more ▼
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
              {historyRuns.length > historyRunsVisible && (
                <button
                  className="history-load-more"
                  onClick={() => setHistoryRunsVisible(v => v + 5)}
                >
                  Load more… ({historyRuns.length - historyRunsVisible} remaining)
                </button>
              )}
            </div>
          )}
        </>
      )}

      {scrapeOpen && uploadEnabled && (
        <div className="scrape-panel">
          <div className="scrape-mode-tabs">
            <button
              className={scrapeMode === 'url' ? 'scrape-mode-tab active' : 'scrape-mode-tab'}
              onClick={() => setScrapeMode('url')}
              title="Scrape from a page URL or playlist.m3u8 URL"
            >
              🔗 URL
            </button>
            <button
              className={scrapeMode === 'curl' ? 'scrape-mode-tab active' : 'scrape-mode-tab'}
              onClick={() => setScrapeMode('curl')}
              title="Paste a curl command copied from browser devtools (Copy as cURL → bash)"
            >
              📋 cURL
            </button>
          </div>
          {scrapeMode === 'url' ? (
            <>
              <input
                className="scrape-input scrape-input-url"
                type="text"
                placeholder="Page URL (https://…)"
                title="Page the video is embedded on. Optional when a playlist URL is given — then it's sent as the Referer."
                value={scrapeForm.pageUrl}
                onChange={e => setScrapeForm(f => ({ ...f, pageUrl: e.target.value }))}
              />
              <input
                className="scrape-input scrape-input-url"
                type="text"
                placeholder="Playlist URL (…/playlist.m3u8) — optional"
                title="Master playlist.m3u8 URL. When set, the backend uses it directly instead of inferring it from the page."
                value={scrapeForm.playlistUrl}
                onChange={e => setScrapeForm(f => ({ ...f, playlistUrl: e.target.value }))}
              />
            </>
          ) : (
            <textarea
              className="scrape-input scrape-input-curl"
              rows={4}
              placeholder={"Paste curl command here (devtools → Network → right-click the playlist .m3u8 request → Copy → Copy as cURL (bash))"}
              title="The playlist URL and request headers are extracted from this curl; the headers are replayed on every request."
              value={scrapeForm.curl}
              onChange={e => setScrapeForm(f => ({ ...f, curl: e.target.value }))}
            />
          )}
          <input
            className="scrape-input"
            type="text"
            placeholder="FOLDER_FILENAME (must contain _)"
            title="Target name following the FOLDER_FILENAME convention — the processor outputs FOLDER/FILENAME.mp4"
            value={scrapeForm.folderFilename}
            onChange={e => setScrapeForm(f => ({ ...f, folderFilename: e.target.value }))}
          />
          <select
            className="scrape-select"
            value={scrapeForm.quality}
            onChange={e => setScrapeForm(f => ({ ...f, quality: e.target.value }))}
          >
            <option value="">Auto (best)</option>
            <option value="1080p">1080p</option>
            <option value="720p">720p</option>
            <option value="480p">480p</option>
            <option value="360p">360p</option>
          </select>
          <button
            className="process-button"
            onClick={startScrape}
            disabled={scrapeActiveCount >= 3 || (scrapeMode === 'curl' ? !scrapeForm.curl.trim() : (!scrapeForm.pageUrl.trim() && !scrapeForm.playlistUrl.trim())) || !scrapeForm.folderFilename.trim()}
            title={scrapeActiveCount >= 3 ? 'Maximum of 3 concurrent scrapes — wait for one to finish' : 'Scrape this URL into the raw queue'}
          >
            🔗 Scrape to Raw Queue
          </button>
        </div>
      )}

      {visibleScrapeJobs.length > 0 && (
        <div className="scrape-jobs-panel">
          <div className="scrape-jobs-header">
            <span>
              {scrapeActiveCount > 0
                ? <>⏳ <strong>{scrapeActiveCount}</strong> scrape{scrapeActiveCount === 1 ? '' : 's'} in progress</>
                : 'Recent scrapes'}
            </span>
            <button
              className="logs-refresh-btn"
              onClick={fetchScrapeStatus}
              disabled={scrapeStatusLoading}
              title="Refresh scrape progress"
            >
              {scrapeStatusLoading ? 'Refreshing…' : '↻ Refresh'}
            </button>
          </div>
          {sessionScrapeJobs.map(renderScrapeJobCard)}
          {earlierScrapeJobs.length > 0 && (
            <>
              <button
                className="scrape-earlier-toggle"
                onClick={() => setEarlierScrapesOpen(v => !v)}
                title={earlierScrapesOpen ? 'Hide scrapes from earlier sessions' : 'Show scrapes from earlier sessions'}
              >
                {earlierScrapesOpen ? '▾' : '▸'} Earlier scrapes ({earlierScrapeJobs.length})
              </button>
              {earlierScrapesOpen && earlierScrapeJobs.map(renderScrapeJobCard)}
            </>
          )}
        </div>
      )}

      {processResult && (
        <div className={`process-result ${processResult.type}`}>
          <p>{processResult.message}</p>
          <button className="process-result-close" onClick={() => setProcessResult(null)}>✕</button>
        </div>
      )}

      {transferStats && (
        <div className="transfer-stats-bar">
          {/* Free Tier Progress Bar — uses best available source */}
          {transferStats.freeTier && transferStats.freeTier.percentUsed != null && (
            <div className="transfer-mtd">
              <div className="transfer-mtd-header">
                <span className="transfer-label">📊 Free Tier MTD Data Out</span>
                <span className={`transfer-mtd-value${transferStats.freeTier.percentUsed >= 90 ? ' danger' : transferStats.freeTier.percentUsed >= 70 ? ' warn' : ''}`}>
                  {transferStats.freeTier.bestBytesOutFormatted}
                  <span className="transfer-mtd-limit"> / {transferStats.freeTier.limitFormatted}</span>
                </span>
              </div>
              <div className="transfer-mtd-bar">
                <div
                  className={`transfer-mtd-fill${transferStats.freeTier.percentUsed >= 90 ? ' danger' : transferStats.freeTier.percentUsed >= 70 ? ' warn' : ''}`}
                  style={{ width: `${Math.min(100, transferStats.freeTier.percentUsed)}%` }}
                />
              </div>
              <div className="transfer-mtd-sub">
                <span>{transferStats.freeTier.percentUsed}% used (CloudWatch)</span>
                <span>{transferStats.freeTier.bytesRemainingFormatted} remaining</span>
              </div>
            </div>
          )}

          {/* Two transfer data sources */}
          <div className="transfer-sources">
            {/* Source 1: S3 CloudWatch Request Metrics (near real-time) */}
            <div className="transfer-source-card">
              <span className="transfer-source-title">☁️ S3 Metrics</span>
              {transferStats.monthToDate ? (
                <>
                  <div className="transfer-source-row">
                    <span className="transfer-source-label">⬇ Out</span>
                    <span className="transfer-source-value">{transferStats.monthToDate.bytesOutFormatted ?? '—'}</span>
                  </div>
                  <div className="transfer-source-row">
                    <span className="transfer-source-label">⬆ In</span>
                    <span className="transfer-source-value">{transferStats.monthToDate.bytesInFormatted ?? '—'}</span>
                  </div>
                  <span className="transfer-source-note">~5 min delay</span>
                </>
              ) : (
                <span className="transfer-source-na" title="Enable S3 Request Metrics with FilterId 'EntireBucket'">not configured</span>
              )}
            </div>

            {/* Source 2: App-tracked (since server start) */}
            <div className="transfer-source-card">
              <span className="transfer-source-title">🖥️ App Tracked</span>
              <div className="transfer-source-row">
                <span className="transfer-source-label">⬇ Out</span>
                <span className="transfer-source-value">{transferStats.app.bytesOutFormatted}</span>
              </div>
              <div className="transfer-source-row">
                <span className="transfer-source-label">⬆ In</span>
                <span className="transfer-source-value">{transferStats.app.bytesInFormatted}</span>
              </div>
              <span className="transfer-source-note">since {new Date(transferStats.app.trackedSince).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
            </div>
          </div>

          {/* Storage + refresh */}
          <div className="transfer-footer">
            {transferStats.storage && transferStats.storage.bucketSizeFormatted && (
              <span className="transfer-cw">
                📦 {transferStats.storage.bucketSizeFormatted}
                {transferStats.storage.numberOfObjects != null && ` · ${Math.round(transferStats.storage.numberOfObjects)} objects`}
                {transferStats.storage.byStorageType && transferStats.storage.byStorageType.length > 0 && (
                  <span className="transfer-storage-breakdown">
                    {transferStats.storage.byStorageType.map((entry) => (
                      <span key={entry.id} className="transfer-storage-class">
                        {entry.label}: {entry.bytesFormatted}
                      </span>
                    ))}
                  </span>
                )}
              </span>
            )}
            {transferStats.disk && (
              <div className="transfer-disk">
                <span className="transfer-label">💽 Server Disk</span>
                <span className={`transfer-disk-value${transferStats.disk.percentUsed >= 90 ? ' danger' : transferStats.disk.percentUsed >= 70 ? ' warn' : ''}`}>
                  {transferStats.disk.usedFormatted} / {transferStats.disk.totalFormatted}
                  <span className="transfer-disk-pct"> ({transferStats.disk.percentUsed}%)</span>
                </span>
              </div>
            )}
            <button className="transfer-refresh" onClick={fetchTransferStats} title="Refresh transfer stats">Refresh</button>
          </div>
        </div>
      )}

      <main className="main-content">
        {/* Video Player */}
        <section className="video-player-section">
          <div className="video-container">
            {selectedVideo ? (
              <video
                ref={videoRef}
                controls
                autoPlay
                crossOrigin="use-credentials"
                key={selectedVideo.id}
                onTimeUpdate={handleTimeUpdate}
                onLoadedMetadata={() => { if (videoRef.current) videoRef.current.volume = volume; }}
                controlsList="nodownload noplaybackrate"
                disablePictureInPicture
              >
                <source src={getStreamUrl(selectedVideo.id)} type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            ) : (
              <div className="no-video-selected">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p>Select a video to play</p>
              </div>
            )}
          </div>
          <div className="video-info">
            {selectedVideo ? (
              <>
                <h2>{selectedVideo.name}</h2>
                <p className="meta">Size: {selectedVideo.sizeFormatted}</p>
              </>
            ) : (
              <div style={{ opacity: 0.5 }}>
                <h2>No video selected</h2>
                <p className="meta">Select a video from the list</p>
              </div>
            )}
            <div className="volume-control">
              <label className="volume-unlock-label">
                <input
                  type="checkbox"
                  checked={volumeUnlocked}
                  onChange={(e) => setVolumeUnlocked(e.target.checked)}
                />
                Adjust Volume
              </label>
              <label htmlFor="volume-slider">🔊 {Math.round(volume * 100)}%</label>
              <input
                id="volume-slider"
                type="range"
                min="0"
                max="1"
                step="0.01"
                value={volume}
                disabled={!volumeUnlocked}
                onChange={(e) => setVolume(parseFloat(e.target.value))}
              />
            </div>
          </div>
        </section>

        {/* Video List */}
        <section className="video-list-section">
          <div className="video-list-header">
            <h3>
              <span>📁</span> {filterLocalOnly ? 'Local Videos' : 'Videos'}
              {sourceCounts.hasSource && sourceCounts.total > 0 && (
                <span className="source-summary" title="Videos migrated to Backblaze B2">
                  {sourceCounts.total} · {sourceCounts.onB2} on B2
                </span>
              )}
            </h3>
            <div className="list-controls">
              <button
                className={filterLocalOnly ? "list-pill active" : "list-pill"}
                onClick={() => setFilterLocalOnly(v => !v)}
                title={filterLocalOnly ? "Show all videos" : "Show only locally cached videos"}
              >
                💾 Local only
              </button>
              <button
                className={showLocalCache ? "list-pill active" : "list-pill"}
                onClick={() => setShowLocalCache(v => !v)}
                title={showLocalCache ? "Hide download/remove buttons" : "Show download/remove buttons on each video"}
              >
                ⬇ Manage
              </button>
              <button
                className={showMigrate ? "list-pill active" : "list-pill"}
                onClick={() => setShowMigrate(v => !v)}
                title={showMigrate ? "Hide S3 → B2 migrate buttons" : "Show S3 → B2 migrate buttons (deletes the S3 original)"}
              >
                🚚 Migrate
              </button>
            </div>
          </div>

          {/* Bulk migration. Sits inside the 🚚 Migrate disclosure with the
              per-row buttons — it is the same destructive control, at scale.
              Single click, like every other destructive action in this app;
              the file count and GB total on the button face are the warning. */}
          {showMigrate && (
            <div className="bulk-migrate-bar">
              <select
                className="bulk-migrate-select"
                value={bulkFolder}
                onChange={(e) => setBulkFolder(e.target.value)}
                disabled={bulkRunning}
                title="Limit the run to one folder"
              >
                <option value="*">All folders</option>
                <option value="">Root (no folder)</option>
                {bulkFolders.map((f) => <option key={f} value={f}>{f}</option>)}
              </select>
              <select
                className="bulk-migrate-select"
                value={bulkMaxGb}
                onChange={(e) => setBulkMaxGb(Number(e.target.value))}
                disabled={bulkRunning}
                title="Cap how many GB this run may copy out of S3. Files already on B2 are verified for free and never count against it."
              >
                <option value={0}>No GB limit</option>
                {[1, 5, 10, 25, 50, 100].map((g) => <option key={g} value={g}>Max {g} GB</option>)}
              </select>

              {bulkRunning ? (
                <>
                  <button
                    className="bulk-migrate-button running"
                    onClick={handleBulkStop}
                    disabled={bulkJob.cancelled || bulkJob.phase === 'finishing'}
                    title="Stop queueing new migrations. Ones already started will finish — there is no way to abort a copy in flight."
                  >
                    {/* 'finishing' means the queue is drained and the server is
                        waiting out the copies still in flight. Nothing left to
                        stop, so don't offer to. */}
                    {bulkJob.phase === 'finishing'
                      ? '⏳ Finishing…'
                      : bulkJob.cancelled
                        ? '⏹ Stopping…'
                        : `⏹ Stop (${bulkJob.started}/${bulkJob.total})`}
                  </button>
                  <span className="bulk-migrate-note">
                    Running on the server — safe to close this tab or log out
                  </span>
                </>
              ) : (
                <button
                  className="bulk-migrate-button"
                  onClick={handleBulkMigrate}
                  disabled={bulkPlan.fileCount === 0}
                  title={bulkPlan.fileCount === 0
                    ? 'Nothing left to migrate in this selection'
                    : 'Copy each file to Backblaze B2, verify it, then permanently delete the original from S3'}
                >
                  {bulkPlan.fileCount === 0
                    ? '🚚 Nothing to migrate'
                    : bulkPlan.copyCount === 0
                      ? `✔️ Verify & free S3 on ${bulkPlan.verifyCount} files`
                      : `🚚 Migrate ${bulkPlan.fileCount} files · ${formatBytes(bulkPlan.copyBytes)} to copy`}
                </button>
              )}
            </div>
          )}

          {loading && (
            <div className="loading">
              <div className="spinner"></div>
              <p>Loading videos...</p>
            </div>
          )}

          {error && (
            <div className="error">
              <p>⚠️ {error}</p>
              <p style={{ fontSize: '0.85rem', marginTop: '10px' }}>
                Make sure the backend server is running on port 3011
              </p>
            </div>
          )}

          {!loading && !error && videos.length === 0 && (
            <div className="empty-list">
              <p>No videos found</p>
              <p>
                Add <code>.mp4</code> files to the <code>videos/</code> folder in the backend
              </p>
            </div>
          )}

          {!loading && !error && videos.length > 0 && filterLocalOnly &&
            groupedVideos.root.length === 0 && groupedVideos.folders.size === 0 && (
            <div className="empty-list">
              <p>No locally cached videos</p>
              <p>Use the download button to cache videos on the server.</p>
            </div>
          )}

          {!loading && !error && videos.length > 0 && (
            <div className="video-navigator">
              {/* Root level videos */}
              {groupedVideos.root.map((video) => renderVideoRow(video))}

              {/* Folders with videos */}
              {[...groupedVideos.folders].map(([folderName, folderVideos]) => (
                <div key={folderName} className="folder-group">
                  <div 
                    className="folder-header"
                    onClick={() => toggleFolder(folderName)}
                  >
                    <span className="folder-icon">
                      {expandedFolders.has(folderName) ? '📂' : '📁'}
                    </span>
                    <span className="folder-name">{folderName}</span>
                    <span className="folder-count">({folderVideos.length})</span>
                    <span className="folder-toggle">
                      {expandedFolders.has(folderName) ? '▼' : '▶'}
                    </span>
                  </div>
                  
                  {expandedFolders.has(folderName) && (
                    <div className="folder-contents">
                      {folderVideos.map((video) => renderVideoRow(video, { nested: true }))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </section>
      </main>
    </div>
  );
}

export default Videos;
